import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  ChartBar, TrendingUp, IndianRupee, Users, Building2, Loader2,
  RefreshCw, Download, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    revenue: { total: 0, thisMonth: 0, growth: 12 },
    students: { total: 0, thisMonth: 0, growth: 8 },
    centers: { total: 0, thisMonth: 0, growth: 5 },
    courses: { enrollments: 0, completions: 0, rate: 78 }
  });

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [studentsRes, centersRes, commissionRes] = await Promise.all([
        axios.get(`${API}/admin/users?role=student`),
        axios.get(`${API}/admin/centers`),
        axios.get(`${API}/admin/commission-report`).catch(() => ({ data: null }))
      ]);

      const students = studentsRes.data || [];
      const centers = centersRes.data || [];
      const now = new Date();

      setReportData({
        revenue: {
          total: commissionRes.data?.total_revenue || 0,
          thisMonth: commissionRes.data?.this_month || 0,
          growth: 12
        },
        students: {
          total: students.length,
          thisMonth: students.filter(s => {
            if (!s.created_at) return false;
            const date = new Date(s.created_at);
            return date.getMonth() === now.getMonth();
          }).length,
          growth: 8
        },
        centers: {
          total: centers.length,
          thisMonth: centers.filter(c => {
            if (!c.created_at) return false;
            const date = new Date(c.created_at);
            return date.getMonth() === now.getMonth();
          }).length,
          growth: 5
        },
        courses: {
          enrollments: 450,
          completions: 350,
          rate: 78
        }
      });
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 mt-1">Platform performance overview</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </Button>
          <Button onClick={fetchReportData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="flex items-center text-emerald-600 text-sm font-medium">
              <ArrowUpRight className="w-4 h-4" />
              {reportData.revenue.growth}%
            </span>
          </div>
          <p className="text-sm text-slate-500">Total Revenue</p>
          <p className="text-2xl font-bold text-slate-900">
            ₹{(reportData.revenue.total / 100000).toFixed(2)}L
          </p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <span className="flex items-center text-emerald-600 text-sm font-medium">
              <ArrowUpRight className="w-4 h-4" />
              {reportData.students.growth}%
            </span>
          </div>
          <p className="text-sm text-slate-500">Total Students</p>
          <p className="text-2xl font-bold text-slate-900">{reportData.students.total}</p>
          <p className="text-xs text-slate-400 mt-1">+{reportData.students.thisMonth} this month</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <span className="flex items-center text-emerald-600 text-sm font-medium">
              <ArrowUpRight className="w-4 h-4" />
              {reportData.centers.growth}%
            </span>
          </div>
          <p className="text-sm text-slate-500">Training Centers</p>
          <p className="text-2xl font-bold text-slate-900">{reportData.centers.total}</p>
          <p className="text-xs text-slate-400 mt-1">+{reportData.centers.thisMonth} this month</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <ChartBar className="w-5 h-5 text-amber-600" />
            </div>
            <span className="flex items-center text-emerald-600 text-sm font-medium">
              {reportData.courses.rate}%
            </span>
          </div>
          <p className="text-sm text-slate-500">Course Completion</p>
          <p className="text-2xl font-bold text-slate-900">{reportData.courses.completions}</p>
          <p className="text-xs text-slate-400 mt-1">of {reportData.courses.enrollments} enrolled</p>
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Revenue Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Course Fees</span>
              <span className="font-semibold">₹{((reportData.revenue.total * 0.6) / 100000).toFixed(2)}L</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Partner Fees</span>
              <span className="font-semibold">₹{((reportData.revenue.total * 0.3) / 100000).toFixed(2)}L</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Certificate Fees</span>
              <span className="font-semibold">₹{((reportData.revenue.total * 0.1) / 100000).toFixed(2)}L</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Growth Trends</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Student Growth</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '75%' }} />
                </div>
                <span className="text-sm font-medium text-emerald-600">+{reportData.students.growth}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Revenue Growth</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: '85%' }} />
                </div>
                <span className="text-sm font-medium text-emerald-600">+{reportData.revenue.growth}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Center Growth</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: '60%' }} />
                </div>
                <span className="text-sm font-medium text-emerald-600">+{reportData.centers.growth}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReportsPage;
